import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { BrandDashboard } from './BrandDashboard';
import { InfluencerDashboard } from './InfluencerDashboard';
import { Spinner } from '../common/Spinner';
import { USER_TYPES } from '../../constants/userTypes';
import { graphqlApi } from '../../services/graphql';

export const Dashboard: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [dbName, setDbName] = useState<string | null>(null);

  useEffect(() => {
    // Simulate loading time - in real app, this would load user data
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Load canonical name from users table; prefer it over auth-store name.
  useEffect(() => {
    let cancelled = false;
    if (!user?.userId) return;
    (async () => {
      try {
        const res = await graphqlApi.getUser(user.userId);
        if (!cancelled && res.success && res.data?.name) {
          setDbName(res.data.name);
        }
      } catch {
        /* ignore — fall back to auth-store name */
      }
    })();
    return () => { cancelled = true; };
  }, [user?.userId]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold text-gray-900">{t('welcome')}</h2>
        <p className="text-gray-600 mt-2">{t('pleaseLogin')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {t('welcomeBack', { name: dbName || user.name || user.email?.split('@')[0] || 'User' })}
        </h1>
        <p className="text-gray-600 mt-2">
          {user.userType === 'host' || user.userType === 'brand' || user.userType === 'agency'
            ? t('hostDescription')
            : t('influencerDescription')
          }
        </p>
      </div>

      {user.userType === USER_TYPES.INFLUENCER ? (
        <InfluencerDashboard />
      ) : (
        <BrandDashboard />
      )}
    </div>
  );
};
